import { HealthMonitorSession } from "@binah/web-sdk";
import useFaceScanStore from "store/face-scan";
import { VideoRecorder } from "./video-recorder";

export interface VideoRecordingSession extends HealthMonitorSession {
  startVideoRecording?(outputPath?: string): Promise<void>;
  stopVideoRecording?(): Promise<Blob | null>;
  getVideoRecordingState?(): boolean;
}

const videoRecorders = new WeakMap<HealthMonitorSession, VideoRecorder>();

export function patchSessionForVideoRecording(
  session: HealthMonitorSession,
  videoElement: HTMLVideoElement
): VideoRecordingSession {
  // Create video recorder instance
  const videoRecorder = new VideoRecorder({
    videoElement,
  });

  videoRecorders.set(session, videoRecorder);

  const originalStart = session.start.bind(session);
  session.start = function () {
    console.log("Video recording: session.start() called");
    const recorder = videoRecorders.get(session);
    if (recorder) {
      recorder.startRecording().catch((error) => {
        console.error("Video recording: Failed to start recording:", error);
      });
    }
    return originalStart();
  };

  const originalStop = session.stop.bind(session);
  session.stop = function () {
    console.log("Video recording: session.stop() called");
    const recorder = videoRecorders.get(session);
    if (recorder) {
      console.log(
        "Video recording: recorder state before stop:",
        recorder.getRecordingState()
      );
      recorder.stopRecording().then(async (blob) => {
        if (blob) {
          console.log(
            "Video recording: Recording stopped, blob size:",
            blob.size
          );

          const presignedUrls = useFaceScanStore.getState().presignedUrls;
          const uploadUrl = presignedUrls.video;


          if (uploadUrl) {
            console.log("Video recording: Uploading to:", uploadUrl);
            try {
              const response = await fetch(uploadUrl, {
                method: "PUT",
                body: blob,
                headers: {
                  "Content-Type": 'video/webm',
                },
              });
              if (response.ok) {
                console.info("Video recording: Upload successful");
              } else {
                console.error(
                  "Video recording: Upload failed",
                  response.statusText
                );
              }
            } catch (err) {
              console.error("Video recording: Upload error", err);
            }
          } else {
            console.warn(
              "Video recording: No upload URL provided, downloading instead"
            );
            // recorder.downloadVideo(blob);
          }
        } else {
          console.warn("Video recording: stopRecording returned null");
        }
      });
    }
    return originalStop();
  };

  (session as VideoRecordingSession).startVideoRecording = async function (
    outputPath?: string
  ): Promise<void> {
    const recorder = videoRecorders.get(session);
    if (!recorder) {
      throw new Error("Video recorder not initialized");
    }
    if (outputPath) {
      (recorder as any).outputPath = outputPath;
    }
    await recorder.startRecording();
  };

  (session as VideoRecordingSession).stopVideoRecording =
    async function (): Promise<Blob | null> {
      const recorder = videoRecorders.get(session);
      if (!recorder) {
        return null;
      }
      return await recorder.stopRecording();
    };

  (session as VideoRecordingSession).getVideoRecordingState =
    function (): boolean {
      const recorder = videoRecorders.get(session);
      if (!recorder) {
        return false;
      }
      return recorder.getRecordingState();
    };

  return session as VideoRecordingSession;
}
